"use client";

import Image from "next/image";
import { Character, CharacterContent, Connection, Group, GroupContent, Media } from "@/src/payload-types";
import React, { useState } from "react";
import { RichText } from "@payloadcms/richtext-lexical/react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import jsxConverters, { useIsMobile } from "@/src/blocks/converters";
import { GroupContentType } from "@/src/collections/GroupContent";
import { GroupWithCharacters } from "@/src/types";
import { SheetContainer } from "@/src/components/Containers";
import { InfoPoint } from "@/src/components/ConsumerMediaDetail";
import { getBlobUrl, getGroupLink } from "@/src/components/utils/utils";
import { Landmark } from "lucide-react";
import Link from "next/link";
import RelationLink from "@/src/components/RelationLink";
import { useLivePreviewContent } from "@/src/utils/useLivePreviewContent";

const FactionDetail = ({
    group,
    content,
    inSheet = true,
}: {
    group: Group;
    content: GroupContent[] | [];
    onCharacterSelect?: (val: Character) => void;
    inSheet: boolean;
}) => {
    const previewContent = useLivePreviewContent(content, "groupContent");
    // A picked tab belongs to the content it was picked from; new content starts on its first tab
    const [pickedTab, setPickedTab] = useState<{ content: typeof content; tab: string }>();
    // the default is the first tab as displayed, i.e. after sorting
    const tab = pickedTab?.content === content ? pickedTab.tab : sortContent(content)[0]?.id.toString();
    const setTab = (value: string) => setPickedTab({ content, tab: value });

    function getContentComponent(g: GroupContent) {
        switch (g.type as keyof typeof GroupContentType) {
            case "FREEFORM":
                return <div />;
            case "CHARACTERS":
                return <FactionMembers group={group} />;
            case "SUMMARY":
                return <ContentSummary group={group} content={g} />;
        }
    }

    const contentToUse = sortContent(previewContent);
    const splash = (group.splash as Media)?.sizes?.splash?.url
        ? (group.splash as Media)?.sizes?.splash
        : (group.splash as Media)?.sizes?.avif;
    const image = splash && getBlobUrl(splash.url!);
    const isMobile = useIsMobile();

    return (
        <SheetContainer inSheet={inSheet} img={image}>
            <Link className="z-20 mt-6 pl-8 mb-[-22px]" href={"/"}>
                Back to web
            </Link>
            <div className="p-4 pr-8 pl-8 pt-0 shrink-0">
                <GroupHeader group={group} splash={splash} image={image} />
            </div>
            <div className="relative pl-8 pr-8">
                {/*Name*/}
                {contentToUse?.length > 0 && (
                    <div>
                        <Tabs key={tab} value={tab} onValueChange={setTab}>
                            <div className="flex pb-4 pt-4">
                                <TabsList>
                                    {contentToUse?.map?.((c) => (
                                        <TabsTrigger
                                            className={`hover:cursor-pointer ${isMobile ? "text-sm p-1" : "text-xl p-5"}`}
                                            key={c.id}
                                            value={c.id.toString()}
                                        >
                                            {c.title || c.type}
                                        </TabsTrigger>
                                    ))}
                                </TabsList>
                            </div>
                            {contentToUse?.map?.((c) => (
                                <TabsContent
                                    forceMount
                                    style={
                                        tab !== c.id.toString()
                                            ? { visibility: "hidden", height: 0, position: "absolute" }
                                            : {}
                                    }
                                    key={c.id}
                                    value={c.id.toString()}
                                >
                                    {getContentComponent(c)}
                                </TabsContent>
                            ))}
                        </Tabs>
                    </div>
                )}
            </div>
        </SheetContainer>
    );
};

export interface GroupHeaderProps {
    group: Group;
    image: undefined | string;
    splash?:
        | {
              url?: string | null;
              width?: number | null;
              height?: number | null;
              mimeType?: string | null;
              filesize?: number | null;
              filename?: string | null;
          }
        | undefined;
}

export const GroupHeader = ({ group, splash, image }: GroupHeaderProps) => {
    const isSmallScreen = useIsMobile(1050);

    return isSmallScreen ? (
        <MobileHeader group={group} splash={splash} image={image} />
    ) : (
        <DesktopHeader group={group} splash={splash} image={image} />
    );
};

const DesktopHeader = ({ group, splash, image }: GroupHeaderProps) => {
    return (
        <>
            <div className="relative flex items-top">
                {/*cover*/}
                <div className="basis-1/2">
                    {splash && <img src={image} alt={(group.splash as Media)?.alt || ""} className="rounded-xl" />}
                </div>
                {/*Info*/}
                <div className="basis-1/3 pt-8 pl-8 flex flex-col gap-4">
                    {group.group && (
                        <InfoPoint title="PART OF" value={getGroupLink(group.group as Group)} icon={<Landmark />} />
                    )}
                </div>
            </div>
            <div className="relative flex flex-col basis-2/3">
                {/*Title*/}
                <div className="pb-4">
                    <div className="text-4xl font-bold">{group.name}</div>
                    <div className="text-xl">{group.title}</div>
                </div>
            </div>
        </>
    );
};

const MobileHeader = ({ group, splash, image }: GroupHeaderProps) => {
    return (
        <>
            <div className="relative flex items-top">
                {/*cover*/}
                <div className="basis-1/1">
                    {splash && <img src={image} alt={(group.splash as Media)?.alt || ""} className="rounded-xl" />}
                </div>
                {/*Info*/}
            </div>
            <div className="relative flex flex-col basis-2/3">
                {/*Title*/}
                <div className="pb-4">
                    <div className="text-4xl font-bold">{group.name}</div>
                    <div className="text-xl">{group.title}</div>
                </div>
                <div className="basis-1/3 flex flex-col gap-4">
                    {group.group && (
                        <InfoPoint title="PART OF" value={getGroupLink(group.group as Group)} icon={<Landmark />} />
                    )}
                </div>
            </div>
        </>
    );
};

const sortContent = (c: GroupContent[]) => {
    // sorts a copy: sorting the content prop in place made content[0] depend on whether it was already rendered
    return [...c].sort((c) => (c.type === "SUMMARY" ? -1 : 1));
};

const ContentSummary = ({ content }: { group: Group; content: GroupContent }) => {
    return (
        <div>
            <div>{content.summary}</div>
            <div>{content.content && <RichText converters={jsxConverters} data={content.content} />}</div>
        </div>
    );
};

const FactionMembers = ({ group }: { group: GroupWithCharacters }) => {
    return (
        <div className="flex flex-col gap-4">
            {group.characters?.map((char) => {
                const charAvatar = (char.avatar as Media).sizes?.avatar;
                return (
                    <div key={char.id} className="flex flex-col h-20">
                        <div className="flex items-center">
                            <div className="p-1 w-18 min-w-18">
                                {charAvatar?.url && (
                                    <img
                                        className="rounded-[50px]"
                                        src={getBlobUrl(charAvatar.url)}
                                        width={charAvatar.width ?? undefined}
                                        height={charAvatar.height ?? undefined}
                                        loading="lazy"
                                        decoding="async"
                                        alt={(char.avatar as Media).alt}
                                    />
                                )}
                            </div>
                            <div className="">
                                <div className="pl-1 text-2xl">
                                    <RelationLink
                                        relationTo={"characters"}
                                        id={char.slug.toString()}
                                        label={char.name}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

const ContentRelationships = ({
    character,
    connections,
    onCharacterSelect,
}: {
    character: Character;
    content: CharacterContent;
    connections?: Connection[];
    onCharacterSelect?: (val: Character) => void;
}) => {
    return (
        <div className="flex flex-col gap-4">
            {connections?.map((con) => (
                <RelationshipView
                    key={con.id}
                    character={character}
                    connection={con}
                    onCharacterSelect={onCharacterSelect}
                />
            ))}
        </div>
    );
};

const RelationshipView = ({
    character,
    connection,
    onCharacterSelect,
}: {
    character: Character;
    connection: Connection;
    onCharacterSelect?: (val: Character) => void;
}) => {
    const otherCharacter =
        (connection.to as Character).id === character.id
            ? (connection.from as Character)
            : (connection.to as Character);

    return (
        <div className="flex flex-col">
            <div className="flex">
                <div className="p-1 w-18 min-w-18">
                    {(otherCharacter.avatar as Media).url && (
                        <Image
                            className="rounded-[50px]"
                            src={(otherCharacter.avatar as Media).url!}
                            width={72}
                            height={72}
                            alt={(otherCharacter.avatar as Media).alt}
                        />
                    )}
                </div>
                <div className="">
                    <div className="pl-1 text-2xl">
                        <button
                            className="hover:cursor-pointer hover:text-blue-400 transition-colors"
                            onClick={() => onCharacterSelect?.(otherCharacter)}
                        >
                            {otherCharacter.name}
                        </button>
                    </div>
                    <div className="pl-1">
                        Dit is lange content over hoe deze mensen elkaar echt niet mogen. Dit is lange content over hoe
                        deze mensen elkaar echt niet mogen. Dit is lange content over hoe deze mensen elkaar echt niet
                        mogen. Dit is lange content over hoe deze mensen elkaar echt niet mogen. Dit is lange content
                        over hoe deze mensen elkaar echt niet mogen. Dit is lange content over hoe deze mensen elkaar
                        echt niet mogen. Dit is lange content over hoe deze mensen elkaar echt niet mogen. Dit is lange
                        content over hoe deze mensen elkaar echt niet mogen. Dit is lange content over hoe deze mensen
                        elkaar echt niet mogen.
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FactionDetail;
